import React, { useState, useRef } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { X, Sparkle, UploadSimple, StackSimple, Globe } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/utils'
import { materialsApi, presentationsApi, getErrorMessage } from '@/lib/api'
import { useTrialStatus } from '@/hooks/useTrialStatus'
import { usePermission } from '@/hooks/usePermission'
import { planLimitToastMessage } from '@/lib/planLimitMessages'
import {
  contentFocusOptions,
  deckToneOptions,
  deckColorThemes,
  deckTargetRoleOptions,
  deckTargetIndustryOptions,
  DECK_SOURCE_CONTENT_TYPES,
} from '@/lib/deckOptions'
import MaterialSelector from '@/pages/workflows/components/config/MaterialSelector'
import { useScrollLock } from '@/lib/useScrollLock'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import {
  GenerateDeckFormState,
  defaultGenerateDeckForm,
  validateGenerateDeckForm,
  buildGenerateDeckPayload,
} from './generateDeckForm'

const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }
const labelClass = 'block mb-1.5 text-muted-foreground dark:text-[#8a8f98]'
const inputClass =
  'w-full px-3 py-2 rounded-md border border-border/60 bg-background text-[13px] text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#5e6ad2] ' +
  'dark:bg-white/[0.03] dark:border-white/[0.08] dark:text-[#f7f8f8]'

interface GenerateDeckModalProps {
  isOpen: boolean
  onClose: () => void
}

export const GenerateDeckModal: React.FC<GenerateDeckModalProps> = ({ isOpen, onClose }) => {
  const [form, setForm] = useState<GenerateDeckFormState>(defaultGenerateDeckForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isTrial } = useTrialStatus()
  // The Upgrade action only appears for whoever can actually act on it:
  // billing.manage_subscription is Owner/Admin, and Billing is hidden in
  // the nav for everyone else. The message still explains the cap.
  const { canWrite: canUpgradePlan } = usePermission('billing', 'manage_subscription')
  useScrollLock(isOpen)

  const set = <K extends keyof GenerateDeckFormState>(key: K, value: GenerateDeckFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const handleClose = () => {
    if (isSubmitting) return
    setForm(defaultGenerateDeckForm)
    onClose()
  }

  const handleLogoUpload = async (file: File) => {
    setIsUploadingLogo(true)
    try {
      const res = await presentationsApi.uploadLogo(file)
      set('logoUrl', res.data.logo_url)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload logo'))
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()

  const handleSubmit = async () => {
    const validationError = validateGenerateDeckForm(form)
    if (validationError) {
      toast.error(validationError)
      return
    }
    setIsSubmitting(true)
    try {
      await materialsApi.generateDeck(buildGenerateDeckPayload(form))
      toast.success('Generating deck. It will appear in Materials shortly.')
      queryClient.invalidateQueries({ queryKey: ['materials'] })
      setForm(defaultGenerateDeckForm)
      onClose()
    } catch (error: any) {
      if (error?.response?.status === 402) {
        toast.error('Not enough credits to generate a deck.')
      } else {
        const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback: getErrorMessage(error, 'Failed to start deck generation') })
        toast.error(message, showUpgrade && canUpgradePlan ? { action: { label: 'Upgrade', onClick: () => navigate('/billing') } } : undefined)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Generate deck with AI"
    >
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={handleClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 bg-card border border-border/60 rounded-2xl shadow-2xl dark:bg-[#0f1011] dark:border-white/[0.08] p-6 w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-foreground dark:text-[#f7f8f8] flex items-center gap-2"
            style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
          >
            <Sparkle className="h-5 w-5 text-[#5e6ad2]" weight="fill" />
            Generate deck with AI
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close generate deck dialog"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Title */}
          <div>
            <label htmlFor="deck-title" className={labelClass} style={labelStyle}>
              Deck title
            </label>
            <input
              id="deck-title"
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Product overview deck"
              className={inputClass}
            />
          </div>

          {/* Output format */}
          <div>
            <span className={labelClass} style={labelStyle}>Output format</span>
            <div className="flex gap-2">
              {(['pdf', 'pptx'] as const).map((fmt) => (
                <button
                  key={fmt}
                  type="button"
                  onClick={() => set('outputFormat', fmt)}
                  aria-pressed={form.outputFormat === fmt}
                  className={cn(
                    'px-4 py-2 rounded-md border text-[13px] font-medium transition-colors duration-150',
                    form.outputFormat === fmt
                      ? 'border-[#5e6ad2] bg-[#5e6ad2]/[0.08] text-foreground dark:text-[#f7f8f8]'
                      : 'border-border/60 dark:border-white/[0.08] text-muted-foreground hover:text-foreground',
                  )}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Source — EITHER selected materials OR a website to scrape.
              Same tab pattern as UploadModal's Upload files / From URL. */}
          <div>
            <span className={labelClass} style={labelStyle}>Source</span>
            <div
              className="flex mb-3 p-1 bg-muted/50 dark:bg-white/[0.03] dark:border dark:border-white/[0.06] rounded-md gap-1"
              role="tablist"
              aria-label="Deck source"
              onKeyDown={(e) =>
                handleTabListKeyDown(e, {
                  activeIndex: form.sourceType === 'materials' ? 0 : 1,
                  count: 2,
                  onSelect: (i) => set('sourceType', i === 0 ? 'materials' : 'website'),
                })
              }
            >
              {([['materials', 'Material sources'], ['website', 'Website link']] as const).map(
                ([type, tabLabel], i) => (
                  <button
                    key={type}
                    role="tab"
                    id={`deck-source-tab-${type}`}
                    aria-selected={form.sourceType === type}
                    aria-controls={`deck-source-panel-${type}`}
                    tabIndex={form.sourceType === type ? 0 : -1}
                    className={cn(
                      'flex-1 inline-flex items-center justify-center gap-2 py-1.5 px-4 rounded border transition-colors duration-150',
                      form.sourceType === type
                        ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
                    )}
                    style={{
                      fontSize: '13px',
                      fontWeight: form.sourceType === type ? 590 : 510,
                      letterSpacing: '-0.13px',
                    }}
                    onClick={() => set('sourceType', type)}
                  >
                    {i === 0
                      ? <StackSimple className="h-4 w-4" weight="regular" />
                      : <Globe className="h-4 w-4" weight="regular" />}
                    {tabLabel}
                  </button>
                ),
              )}
            </div>

            {form.sourceType === 'materials' ? (
              <div id="deck-source-panel-materials" role="tabpanel" aria-labelledby="deck-source-tab-materials">
                <MaterialSelector
                  selectedIds={form.materialIds}
                  onChange={(ids) => set('materialIds', ids)}
                  allowedContentTypes={DECK_SOURCE_CONTENT_TYPES}
                />
              </div>
            ) : (
              <div id="deck-source-panel-website" role="tabpanel" aria-labelledby="deck-source-tab-website">
                <label htmlFor="deck-website-url" className={labelClass} style={labelStyle}>
                  Website URL
                </label>
                <input
                  id="deck-website-url"
                  type="url"
                  value={form.websiteUrl}
                  onChange={(e) => set('websiteUrl', e.target.value)}
                  placeholder="https://example.com"
                  className={inputClass}
                />
                <p className="mt-1.5 text-[11px] text-muted-foreground dark:text-[#8a8f98]">
                  We'll scrape this site's main pages before generating and build the
                  deck from its content.
                </p>
              </div>
            )}
          </div>

          {/* Audience targeting — pre-defined dropdowns, baked into the
              deck's content at generation time. The deck is a plain shared
              file: no per-recipient merge fields (removed 2026-08-25 so
              sends do zero per-contact work). */}
          <div className="space-y-3 rounded-lg border border-border/60 dark:border-white/[0.08] p-3.5">
            <span className="text-[13px] font-medium text-foreground dark:text-[#f7f8f8]">Audience</span>
            {/* Stacked full-width (not a 2-col grid): the option labels are
                long ("Engineering Leadership (CTO / VP Engineering)") and
                the Select truncates whatever doesn't fit its container. */}
            <div>
              <label htmlFor="deck-target-role" className={labelClass} style={labelStyle}>
                Target role
              </label>
              {/* w-full: the Select root is inline-block and shrinks to the
                  selected label; the open menu is sized to the trigger, so
                  stretching the root is what lets long option names render
                  fully in the list. */}
              <Select
                id="deck-target-role"
                value={form.targetRole}
                onChange={(v) => set('targetRole', v)}
                options={deckTargetRoleOptions}
                ariaLabel="Target role"
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="deck-target-industry" className={labelClass} style={labelStyle}>
                Target industry
              </label>
              <Select
                id="deck-target-industry"
                value={form.targetIndustry}
                onChange={(v) => set('targetIndustry', v)}
                options={deckTargetIndustryOptions}
                ariaLabel="Target industry"
                className="w-full"
              />
            </div>
            <p className="text-[11px] text-muted-foreground dark:text-[#8a8f98]">
              The deck's content is tailored to the selected role and industry.
            </p>
          </div>

          {/* Slide count */}
          <div>
            <label htmlFor="deck-slide-count" className={labelClass} style={labelStyle}>
              Number of slides: {form.slideCount}
            </label>
            <input
              id="deck-slide-count"
              type="range"
              min={3}
              max={15}
              value={form.slideCount}
              onChange={(e) => set('slideCount', Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Focus + tone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="deck-focus" className={labelClass} style={labelStyle}>Content focus</label>
              <Select
                id="deck-focus"
                value={form.contentFocus}
                onChange={(v) => set('contentFocus', v as GenerateDeckFormState['contentFocus'])}
                options={contentFocusOptions}
                ariaLabel="Content focus"
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="deck-tone" className={labelClass} style={labelStyle}>Tone</label>
              <Select
                id="deck-tone"
                value={form.tone}
                onChange={(v) => set('tone', v as GenerateDeckFormState['tone'])}
                options={deckToneOptions}
                ariaLabel="Tone"
                className="w-full"
              />
            </div>
          </div>

          {/* Color theme */}
          <div>
            <span className={labelClass} style={labelStyle}>Color theme</span>
            <div className="grid grid-cols-3 gap-2">
              {deckColorThemes.map((theme) => (
                <button
                  key={theme.value}
                  type="button"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      colorTheme: theme.value,
                      customColors:
                        theme.value === 'custom'
                          ? f.customColors ?? { primary: '#1E40AF', accent: '#3B82F6', secondary: '#0F172A' }
                          : undefined,
                    }))
                  }}
                  aria-pressed={form.colorTheme === theme.value}
                  className={cn(
                    'flex items-center gap-2 px-2.5 py-2 rounded-md border text-left transition-colors duration-150',
                    form.colorTheme === theme.value
                      ? 'border-[#5e6ad2] bg-[#5e6ad2]/[0.08]'
                      : 'border-border/60 dark:border-white/[0.08] hover:bg-muted/40',
                  )}
                >
                  <span className="flex -space-x-1 flex-shrink-0">
                    <span className="w-3.5 h-3.5 rounded-full border border-white/40" style={{ backgroundColor: theme.primary }} />
                    <span className="w-3.5 h-3.5 rounded-full border border-white/40" style={{ backgroundColor: theme.accent }} />
                  </span>
                  <span className="text-[11px] font-medium text-foreground dark:text-[#f7f8f8] truncate">{theme.label}</span>
                </button>
              ))}
            </div>
            {form.colorTheme === 'custom' && form.customColors && (
              <div className="mt-2 flex gap-3">
                {(['primary', 'accent', 'secondary'] as const).map((key) => (
                  <label key={key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground capitalize">
                    <input
                      type="color"
                      value={form.customColors![key]}
                      onChange={(e) =>
                        set('customColors', { ...form.customColors!, [key]: e.target.value })
                      }
                      aria-label={`Custom ${key} color`}
                    />
                    {key}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Logo */}
          <div>
            <span className={labelClass} style={labelStyle}>Logo (optional)</span>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleLogoUpload(file)
                e.target.value = ''
              }}
            />
            {form.logoUrl ? (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-foreground dark:text-[#f7f8f8] truncate">{form.logoUrl.split('/').pop()}</span>
                <button
                  type="button"
                  onClick={() => set('logoUrl', '')}
                  aria-label="Remove logo"
                  className="p-1 rounded text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                loading={isUploadingLogo}
                icon={<UploadSimple className="h-4 w-4" weight="regular" />}
                onClick={() => logoInputRef.current?.click()}
              >
                Upload logo
              </Button>
            )}
          </div>

          {/* Custom instructions */}
          <div>
            <label htmlFor="deck-instructions" className={labelClass} style={labelStyle}>
              Custom instructions (optional)
            </label>
            <textarea
              id="deck-instructions"
              rows={3}
              value={form.customInstructions}
              onChange={(e) => set('customInstructions', e.target.value)}
              placeholder="Anything specific the deck should cover or avoid..."
              className={inputClass}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[11px] text-muted-foreground dark:text-[#8a8f98]">
              Takes a few minutes · uses credits
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              {/* Disabled until the ACTIVE source tab is filled in — a deck
                  without a source would be generic filler. */}
              <Button
                variant="default"
                size="sm"
                loading={isSubmitting}
                disabled={
                  planLapsed ||
                  (form.sourceType === 'materials'
                    ? form.materialIds.length === 0
                    : !form.websiteUrl.trim())
                }
                title={
                  planLapsed
                    ? lapsedTitle
                    : form.sourceType === 'materials' && form.materialIds.length === 0
                    ? 'Select at least one material source first'
                    : form.sourceType === 'website' && !form.websiteUrl.trim()
                      ? 'Enter the website URL to scrape first'
                      : undefined
                }
                icon={<Sparkle className="h-4 w-4" weight="fill" />}
                onClick={handleSubmit}
              >
                Generate deck
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}
